import type {
  GradeElementaryMiddle,
  SchoolType,
} from '@mathflat/domain/@entities/SchoolSystem/schema'
import type { MathflatApi } from '@mathflat/domain/@entities/Student/api'
import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain.ts'
import type { Entity } from '@mathflat/domain/@entities/Student/dto'
import type { DefaultResponse } from '@mathflat/shared/@types/mathflatApi'

import maxios from '../utils/maxios'

export const studentApi = {
  getStudentAppSettings: async (studentId: StudentDomain.Id) => {
    const res = await maxios.get<DefaultResponse<MathflatApi.Response.StudentAppSetting>>(
      `/students/app-setting/${studentId}`,
    )
    const data = {
      일반채점_채점전정답공개: res.data.showSolutionBeforeScoring,
      채점후정답해설공개: res.data.showSolutionAfterScoring,
      채점전문풀동공개: res.data.showProblemVideoBeforeScoring,
      채점후문풀동공개: res.data.showProblemVideoAfterScoring,
      학부모정답해설공개: res.data.showSolutionToParent,
      studentId: res.data.studentId,
      lectureService: res.data.lectureService,
      개념힌트_사용: res.data.useAiForHintConceptual,
      풀이분석_사용: res.data.useAiForSolveTheStuckUp,
      풀이확인_사용: res.data.useAiForReviewTheSolution,
    }
    // [DEV ONLY] QA 도구: studentAppSetting 4 플래그를 임시로 override 한다.
    //   - production 빌드에선 import.meta.env.DEV=false 라 통째로 tree-shaken (dead code)
    //   - localStorage 에 저장하면 page reload 후에도 유지, globalThis 는 페이지 reload 시 휘발
    //
    // 사용 예 (브라우저 dev console):
    //   localStorage.setItem('__APP_SETTING_OVERRIDE__', JSON.stringify({
    //     일반채점_채점전정답공개: false,
    //     채점후정답해설공개:     false,
    //     채점전문풀동공개:       false,
    //     채점후문풀동공개:       false,
    //   }))
    //   // 학습지/교재 진입 시 override 적용된 setting 으로 ViewOption 생성됨.
    //   // 끝나면 정리:
    //   localStorage.removeItem('__APP_SETTING_OVERRIDE__')
    //
    // 자세한 가이드: apps/mathflat-student/.claude/tc/05-testing-guide.md
    if (import.meta.env.DEV) {
      let override = (globalThis as { __APP_SETTING_OVERRIDE__?: Partial<typeof data> })
        .__APP_SETTING_OVERRIDE__
      if (!override) {
        try {
          const stored = localStorage.getItem('__APP_SETTING_OVERRIDE__')
          if (stored) override = JSON.parse(stored) as Partial<typeof data>
        } catch {
          /* ignore parse errors */
        }
      }
      if (override) {
        return { ...data, ...override }
      }
    }
    return data
  },

  getStudentById: (studentId: Entity.Student['id']) => {
    return maxios.get<DefaultResponse<MathflatApi.Response.Student>>(`/students/${studentId}`)
  },

  patchStudentById: async (
    studentId: Entity.Student['id'],
    payload: {
      schoolType: SchoolType
      grade: GradeElementaryMiddle
    },
  ) => {
    await maxios.patch<
      unknown,
      unknown,
      {
        schoolType: SchoolType
        grade: GradeElementaryMiddle
      }
    >(`/students/${studentId}`, payload)
  },

  updateLearningTime: async (minute: number) => {
    await maxios.post<null>('/student-learning/time', null, {
      params: {
        minute,
      },
    })
  },

  updateDailyLearningTime: async (minute: number) => {
    await maxios.post<null>('/student-learning/time/daily', null, {
      params: {
        minute,
      },
    })
  },
}
